import styled from "styled-components";

// Building blocks for the moderation grids (photos, reels, videos): a card
// with a media frame, badges on top and actions that appear on hover.

export const MediaGrid = styled.div<{ $min?: number; $gap?: number }>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${(props) => props.$min ?? 260}px, 1fr)
  );
  gap: ${(props) => props.$gap ?? 16}px;
  align-items: start;
`;

export const MediaCard = styled.article<{ $accent?: string }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
  transition: border-color 0.15s ease;
  ${(props) =>
    props.$accent ? `border-top: 3px solid ${props.$accent} !important;` : ""}

  &:hover {
    border-color: ${(props) => props.theme.borderStrong};
  }
`;

// iPhone portrait (9:19.5) by default, since that is how photos show in app.
export const MediaFrame = styled.div<{ $ratio?: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${(props) => props.$ratio ?? "9 / 19.5"};
  overflow: hidden;
  background: ${(props) => props.theme.surfaceRaised};
  cursor: pointer;
`;

export const MediaImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

// Always visible strip at the top of the frame (badges).
export const MediaTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  padding: 10px;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

export const MediaBadges = styled.div<{ $align?: "start" | "end" }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) =>
    props.$align === "end" ? "flex-end" : "flex-start"};
  gap: 4px;
  min-width: 0;
`;

// Hover overlay anchored to the bottom of the frame, holds the actions.
export const MediaActions = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 44px 10px 12px;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.82) 0%,
    rgba(0, 0, 0, 0.55) 55%,
    rgba(0, 0, 0, 0) 100%
  );
  opacity: 0;
  transition: opacity 0.14s ease;
  pointer-events: none;

  ${MediaFrame}:hover &,
  ${MediaFrame}:focus-within & {
    opacity: 1;
    pointer-events: auto;
  }

  /* No hover on touch screens, so keep the actions reachable. */
  @media (hover: none) {
    opacity: 1;
    pointer-events: auto;
  }
`;

// Hover overlay anchored to the top-left corner (open user, search, ...).
export const MediaCornerActions = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 4;
  display: flex;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.14s ease;
  pointer-events: none;

  ${MediaFrame}:hover &,
  ${MediaFrame}:focus-within & {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const MediaActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const MediaActionLabel = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
`;

export const MediaFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px 12px 12px;
  min-width: 0;
`;

export const MediaFooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const MediaTitle = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13.5px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

export const MediaMeta = styled.span`
  font-size: 12px;
  line-height: 1.4;
  color: ${(props) => props.theme.secondaryTextColor};
  overflow-wrap: anywhere;
`;

// Round translucent chip for a single icon on top of media (platform, play).
export const MediaIconChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(17, 19, 24, 0.72);
  backdrop-filter: blur(6px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  color: #fff;
`;
